/**
 * FounderOnboardingWizard (gt-per97)
 *
 * Post-onboarding deep-dive wizard for startup founders.
 * Shown AFTER the main onboarding as a specialized flow.
 *
 * Steps:
 * 1. Connect Repos - prompt to import startup repos
 * 2. Define Product - product name, one-line description, target market
 * 3. Funding Stage - select current fundraising stage
 * 4. Competitors - add 1-3 competitor names
 * 5. Ready - summary of selections, "Generate Investor Report" CTA
 */

import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Rocket, Code2, DollarSign, Check, ChevronRight, ChevronLeft,
  X, Target, Sparkles, Building2, TrendingUp, Zap,
  GitBranch, Shield, Upload, Plus, Trash2, FileText,
  Globe, Users, Award,
} from 'lucide-react';
import Button from '../ui/Button';

export const FUNDING_STAGES = [
  { id: 'pre-seed', label: 'Pre-seed', description: 'Building MVP, validating idea', icon: Sparkles },
  { id: 'seed', label: 'Seed', description: 'Early traction, first customers', icon: Target },
  { id: 'series-a', label: 'Series A', description: 'Product-market fit, scaling', icon: TrendingUp },
  { id: 'series-b', label: 'Series B+', description: 'Growth stage, expanding market', icon: Zap },
  { id: 'bootstrapped', label: 'Bootstrapped', description: 'Self-funded, profitable or lean', icon: Building2 },
];

export const PRODUCT_TYPES = [
  { id: 'saas', label: 'SaaS Platform', icon: Globe },
  { id: 'marketplace', label: 'Marketplace', icon: Users },
  { id: 'dev-tools', label: 'Developer Tools', icon: Code2 },
  { id: 'ai-ml', label: 'AI / ML', icon: Sparkles },
  { id: 'fintech', label: 'FinTech', icon: DollarSign },
  { id: 'other', label: 'Other', icon: Award },
];

const STEPS = [
  { id: 'repos', title: 'Repos', icon: GitBranch },
  { id: 'product', title: 'Product', icon: Code2 },
  { id: 'funding', title: 'Funding', icon: DollarSign },
  { id: 'competitors', title: 'Competitors', icon: Target },
  { id: 'ready', title: 'Ready', icon: Rocket },
];

/** Progress stepper matching OnboardingWizard style */
const ProgressStepper = ({ currentStep, steps }) => (
  <div className="flex items-center justify-center gap-2 mb-8">
    {steps.map((step, index) => {
      const isCompleted = index < currentStep;
      const isCurrent = index === currentStep;
      const Icon = step.icon;
      return (
        <React.Fragment key={step.id}>
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
              isCompleted
                ? 'bg-success-500 border-success-500'
                : isCurrent
                ? 'bg-primary-500/20 border-primary-500'
                : 'bg-white/5 border-white/20'
            }`}
            aria-current={isCurrent ? 'step' : undefined}
          >
            {isCompleted ? (
              <Check className="w-5 h-5 text-white" />
            ) : (
              <Icon className={`w-5 h-5 ${isCurrent ? 'text-primary-400' : 'text-gray-500'}`} />
            )}
          </div>
          {index < steps.length - 1 && (
            <div className={`w-8 h-0.5 transition-colors duration-300 ${
              isCompleted ? 'bg-success-500' : 'bg-white/20'
            }`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

/** Step 1: Connect Repos */
const ConnectReposStep = ({ onNext, onSkip }) => (
  <div className="text-center">
    <div className="mb-6">
      <div className="inline-flex p-4 rounded-full bg-success-500/20 mb-4">
        <Upload className="w-12 h-12 text-success-400" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">Connect Your Startup Repos</h2>
      <p className="text-gray-400 max-w-md mx-auto">
        Import your startup's repositories so we can analyze your tech stack, team velocity, and code maturity.
      </p>
    </div>

    <div className="bg-white/5 rounded-xl border border-white/10 p-5 mb-6 text-left max-w-md mx-auto space-y-3">
      <div className="flex items-start gap-3">
        <GitBranch className="w-5 h-5 text-success-400 mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-sm font-medium text-white">Import organization repositories</div>
          <div className="text-xs text-gray-500">Connect your GitHub org or add repos individually</div>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <TrendingUp className="w-5 h-5 text-secondary-400 mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-sm font-medium text-white">Automated tech due diligence</div>
          <div className="text-xs text-gray-500">We analyze code patterns, architecture, and team contributions</div>
        </div>
      </div>
      <div className="flex items-start gap-3">
        <Shield className="w-5 h-5 text-primary-400 mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-sm font-medium text-white">Code stays private</div>
          <div className="text-xs text-gray-500">We analyze metadata and patterns, never store source code</div>
        </div>
      </div>
    </div>

    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Button variant="gradient" size="lg" onClick={onNext} icon={ChevronRight} iconPosition="right">
        Continue
      </Button>
      <Button variant="ghost" size="md" onClick={onSkip}>
        I'll do this later
      </Button>
    </div>
  </div>
);

/** Step 2: Define Product */
const DefineProductStep = ({ productName, setProductName, description, setDescription, targetMarket, setTargetMarket, onNext, onBack }) => (
  <div className="text-center">
    <div className="mb-6">
      <div className="inline-flex p-4 rounded-full bg-secondary-500/20 mb-4">
        <Code2 className="w-12 h-12 text-secondary-400" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">Define Your Product</h2>
      <p className="text-gray-400 max-w-md mx-auto">
        Help us understand what you're building so we can generate relevant investor narratives.
      </p>
    </div>

    <div className="max-w-md mx-auto mb-6 space-y-4">
      <div>
        <label htmlFor="founder-product-name" className="block text-sm font-medium text-gray-300 text-left mb-1">
          Product Name
        </label>
        <input
          id="founder-product-name"
          type="text"
          value={productName}
          onChange={(e) => setProductName(e.target.value)}
          placeholder="e.g. Acme Platform"
          className="w-full px-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary-500/50 transition-colors"
        />
      </div>
      <div>
        <label htmlFor="founder-description" className="block text-sm font-medium text-gray-300 text-left mb-1">
          One-Line Description
        </label>
        <input
          id="founder-description"
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="e.g. AI-powered developer productivity tool"
          className="w-full px-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary-500/50 transition-colors"
        />
      </div>
      <div>
        <label htmlFor="founder-target-market" className="block text-sm font-medium text-gray-300 text-left mb-1">
          Target Market
        </label>
        <input
          id="founder-target-market"
          type="text"
          value={targetMarket}
          onChange={(e) => setTargetMarket(e.target.value)}
          placeholder="e.g. Enterprise engineering teams"
          className="w-full px-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary-500/50 transition-colors"
        />
      </div>
    </div>

    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Button variant="ghost" size="md" onClick={onBack} icon={ChevronLeft}>Back</Button>
      <Button variant="gradient" size="md" onClick={onNext} icon={ChevronRight} iconPosition="right">
        Continue
      </Button>
    </div>
  </div>
);

/** Step 3: Funding Stage */
const FundingStep = ({ fundingStage, setFundingStage, onNext, onBack }) => (
  <div className="text-center">
    <div className="mb-6">
      <div className="inline-flex p-4 rounded-full bg-warning-500/20 mb-4">
        <DollarSign className="w-12 h-12 text-warning-400" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2">What's Your Funding Stage?</h2>
      <p className="text-gray-400 max-w-md mx-auto">
        This shapes the investor narrative we generate for your startup.
      </p>
    </div>

    <div className="max-w-md mx-auto space-y-2 mb-6">
      {FUNDING_STAGES.map((stage) => {
        const Icon = stage.icon;
        const isSelected = fundingStage === stage.id;
        return (
          <button
            key={stage.id}
            onClick={() => setFundingStage(stage.id)}
            className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
              isSelected
                ? 'border-primary-500 bg-primary-500/10'
                : 'border-white/10 bg-white/[0.03] hover:border-white/20'
            }`}
            aria-pressed={isSelected}
          >
            <Icon className={`w-6 h-6 flex-shrink-0 ${isSelected ? 'text-primary-400' : 'text-gray-400'}`} />
            <div className="flex-1">
              <div className="text-sm font-medium text-white">{stage.label}</div>
              <div className="text-xs text-gray-500">{stage.description}</div>
            </div>
            {isSelected && <Check className="w-5 h-5 text-primary-400 flex-shrink-0" />}
          </button>
        );
      })}
    </div>

    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Button variant="ghost" size="md" onClick={onBack} icon={ChevronLeft}>Back</Button>
      <Button variant="gradient" size="md" onClick={onNext} disabled={!fundingStage} icon={ChevronRight} iconPosition="right">
        Continue
      </Button>
    </div>
  </div>
);

/** Step 4: Competitors */
const CompetitorsStep = ({ competitors, setCompetitors, onNext, onBack }) => {
  const addCompetitor = () => {
    if (competitors.length < 3) {
      setCompetitors([...competitors, '']);
    }
  };

  const updateCompetitor = (index, value) => {
    const updated = [...competitors];
    updated[index] = value;
    setCompetitors(updated);
  };

  const removeCompetitor = (index) => {
    setCompetitors(competitors.filter((_, i) => i !== index));
  };

  return (
    <div className="text-center">
      <div className="mb-6">
        <div className="inline-flex p-4 rounded-full bg-error-500/20 mb-4">
          <Target className="w-12 h-12 text-error-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Who Are Your Competitors?</h2>
        <p className="text-gray-400 max-w-md mx-auto">
          Add up to 3 competitors so we can position your technical advantages.
        </p>
      </div>

      <div className="max-w-md mx-auto mb-6 space-y-3">
        {competitors.map((name, index) => (
          <div key={index} className="flex items-center gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => updateCompetitor(index, e.target.value)}
              placeholder={`Competitor ${index + 1}`}
              className="flex-1 px-4 py-3 bg-white/[0.06] border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-primary-500/50 transition-colors"
              aria-label={`Competitor ${index + 1} name`}
            />
            <button
              onClick={() => removeCompetitor(index)}
              className="p-2.5 rounded-lg text-gray-400 hover:text-error-400 hover:bg-error-500/10 transition-colors"
              aria-label={`Remove competitor ${index + 1}`}
            >
              <Trash2 className="w-5 h-5" />
            </button>
          </div>
        ))}

        {competitors.length < 3 && (
          <button
            onClick={addCompetitor}
            className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-white/10 text-gray-400 hover:border-white/20 hover:text-white transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm">Add Competitor</span>
          </button>
        )}

        <p className="text-xs text-gray-500 mt-2">
          This is optional. You can skip if you prefer not to share competitors now.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button variant="ghost" size="md" onClick={onBack} icon={ChevronLeft}>Back</Button>
        <Button variant="gradient" size="md" onClick={onNext} icon={ChevronRight} iconPosition="right">
          Continue
        </Button>
      </div>
    </div>
  );
};

/** Step 5: Ready */
const ReadyStep = ({ data, onComplete }) => {
  const stage = FUNDING_STAGES.find(s => s.id === data.fundingStage);
  const filledCompetitors = data.competitors.filter(c => c.trim());

  return (
    <div className="text-center">
      <div className="mb-6">
        <div className="inline-flex p-4 rounded-full bg-primary-500/20 mb-4">
          <Rocket className="w-12 h-12 text-primary-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Ready to Generate Your Report</h2>
        <p className="text-gray-400 max-w-md mx-auto">
          Here's a summary of what we'll use to build your investor narrative.
        </p>
      </div>

      <div className="bg-white/5 rounded-xl border border-white/10 p-5 mb-6 max-w-md mx-auto text-left space-y-3">
        {data.productName && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Product:</span>
            <span className="text-white font-medium">{data.productName}</span>
          </div>
        )}
        {data.description && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Description:</span>
            <span className="text-white font-medium">{data.description}</span>
          </div>
        )}
        {data.targetMarket && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Target Market:</span>
            <span className="text-white font-medium">{data.targetMarket}</span>
          </div>
        )}
        {stage && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Funding Stage:</span>
            <span className="text-white font-medium">{stage.label}</span>
          </div>
        )}
        {filledCompetitors.length > 0 && (
          <div className="flex items-start gap-2 text-sm">
            <span className="text-gray-500">Competitors:</span>
            <span className="text-white font-medium">{filledCompetitors.join(', ')}</span>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <Button variant="gradient" size="lg" onClick={onComplete} icon={FileText}>
          Generate Investor Report
        </Button>
      </div>
    </div>
  );
};

/**
 * Main FounderOnboardingWizard Component
 *
 * A deeper onboarding wizard for founders, shown after the main onboarding
 * as a post-onboarding deep-dive.
 */
const FounderOnboardingWizard = ({ isOpen, onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [founderData, setFounderData] = useState({
    productName: '',
    description: '',
    targetMarket: '',
    fundingStage: null,
    competitors: [''],
  });

  const updateField = useCallback((field, value) => {
    setFounderData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleComplete = () => {
    const result = {
      ...founderData,
      competitors: founderData.competitors.filter(c => c.trim()),
      completedAt: new Date().toISOString(),
    };
    onComplete(result);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" aria-hidden="true" />
      <div className="relative bg-gray-900 border border-white/10 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <button
          onClick={onSkip}
          className="absolute top-4 right-4 p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors z-10"
          aria-label="Skip founder setup"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="p-6 sm:p-8">
          <ProgressStepper currentStep={currentStep} steps={STEPS} />

          <div className="min-h-[300px]">
            {currentStep === 0 && (
              <ConnectReposStep
                onNext={() => setCurrentStep(1)}
                onSkip={onSkip}
              />
            )}
            {currentStep === 1 && (
              <DefineProductStep
                productName={founderData.productName}
                setProductName={(v) => updateField('productName', v)}
                description={founderData.description}
                setDescription={(v) => updateField('description', v)}
                targetMarket={founderData.targetMarket}
                setTargetMarket={(v) => updateField('targetMarket', v)}
                onNext={() => setCurrentStep(2)}
                onBack={() => setCurrentStep(0)}
              />
            )}
            {currentStep === 2 && (
              <FundingStep
                fundingStage={founderData.fundingStage}
                setFundingStage={(v) => updateField('fundingStage', v)}
                onNext={() => setCurrentStep(3)}
                onBack={() => setCurrentStep(1)}
              />
            )}
            {currentStep === 3 && (
              <CompetitorsStep
                competitors={founderData.competitors}
                setCompetitors={(v) => updateField('competitors', v)}
                onNext={() => setCurrentStep(4)}
                onBack={() => setCurrentStep(2)}
              />
            )}
            {currentStep === 4 && (
              <ReadyStep
                data={founderData}
                onComplete={handleComplete}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

FounderOnboardingWizard.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onComplete: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
};

export default FounderOnboardingWizard;
